import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import { ChatBubble } from "./chatBubble";

export interface TooltipProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'content'> {
  asChild?: boolean;
  id: string;
  title: string;
  content: React.ReactNode;
  children: React.ReactNode;
}

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  (
    { className, asChild = false, id, title, content, children, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "div";
    const [isShow, setIsShow] = React.useState(false);
    const [position, setPosition] = React.useState<'center' | 'left' | 'right'>('center');
    const tooltipRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLDivElement>(null);

    const updatePosition = React.useCallback(() => {
      if (!tooltipRef.current || !triggerRef.current) return;

      const trigger = triggerRef.current.getBoundingClientRect();
      const tooltip = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Calculate positions
      const centerPos = trigger.left + (trigger.width / 2) - (tooltip.width / 2);
      const rightPos = trigger.right - tooltip.width;
      const leftPos = trigger.left;

      // Check if center position causes overflow
      if (centerPos + tooltip.width > viewportWidth - 20) {
        // Right side overflow - align to right
        setPosition('right');
      } else if (centerPos < 20) {
        // Left side overflow - align to left
        setPosition('left');
      } else {
        // No overflow - center align
        setPosition('center');
      }
    }, []);

    React.useEffect(() => {
      if (isShow) {
        updatePosition();
        window.addEventListener('resize', updatePosition);
        return () => window.removeEventListener('resize', updatePosition);
      }
    }, [isShow, updatePosition]);

    const getPositionClasses = () => {
      switch (position) {
        case 'left':
          return 'left-0';
        case 'right':
          return 'right-0';
        default:
          return 'left-1/2 -translate-x-1/2';
      }
    };

    return (
      <Comp className={cn("relative", className)} ref={ref} {...props}>
        <div 
          ref={triggerRef}
          className="cursor-pointer"
          onMouseEnter={() => setIsShow(true)}
          onMouseLeave={() => setIsShow(false)}
          onClick={() => setIsShow(!isShow)}
        >
          {children}
        </div>
        {isShow && (
          <div 
            ref={tooltipRef}
            className={cn(
              "absolute z-50 bottom-full mb-2",
              getPositionClasses()
            )}
          >
            <ChatBubble
              id={id}
              title={title}
              content={content}
              onOk={() => setIsShow(false)}
              className="px-4 md:px-0"
            />
          </div>
        )}
      </Comp>
    );
  }
);

Tooltip.displayName = "Tooltip";

export { Tooltip };
